// What a customer's copy of a quote (c/{customerID}/quotes/{quoteID}) must
// never carry: our costs (a labor line's material and equipment costs
// too), margins and markups, the vendor's list price, who does the labor,
// and the buyer's contact ID. A markup gives the cost away as surely as the
// cost does: cost = price / (1 + markup / 100). Quote.toCustomerQuote scrubs the quote's
// own items and total with these, and every item and total inside its
// change-order `changes` too -- those once kept margin, retail and the
// vendors' costs. scripts/repair-customer-quote-copies.mjs scrubs the copies
// written before that.
//
// No imports, so the repair script can load it as is.

export const INTERNAL_ITEM_FIELDS = Object.freeze([
  "cost",
  "materialCost",
  "equipmentCost",
  "margin",
  "markup",
  "retail",
  "laborers",
]);

export const INTERNAL_TOTAL_FIELDS = Object.freeze([
  "cost",
  "margin",
  "shippingCost",
  "additionalCost",
  "actualCost",
  "actualPreTax",
  "vendors",
  "expenseCosts",
]);

const without = (object, fields) => {
  if (!object || typeof object !== "object") return object;
  const copy = { ...object };
  fields.forEach((field) => delete copy[field]);
  return copy;
};

export const scrubCustomerItem = (item) => without(item, INTERNAL_ITEM_FIELDS);

export const scrubCustomerTotal = (total) =>
  without(total, INTERNAL_TOTAL_FIELDS);

// A bill-to or ship-to keeps the customer's name, never their contact ID
const scrubAddressee = (side) =>
  side && side.address && typeof side.address === "object"
    ? { ...side, address: without(side.address, ["contact"]) }
    : side;

// A modified item's diff that the scrub has emptied
const isEmptyDiff = (side) =>
  !!side &&
  typeof side === "object" &&
  !Array.isArray(side) &&
  !Object.keys(side).length;

// Changes that hold no item or total (Quote.getChanges); every other entry's
// id is an item's
const PASSED_THROUGH = new Set([
  "deliveryContact",
  "attachments",
  "terms",
  "holdScreens",
  "holdHardware",
]);

/**
 * A change-order `changes` list as a customer may see it. An entry's
 * `before` and `after` are whole items (added, removed), item diffs
 * (modified), totals, or bill-to / ship-to (diffs).
 */
export function scrubCustomerQuoteChanges(changes) {
  if (!Array.isArray(changes)) return changes;
  return changes.flatMap((change) => {
    if (!change || typeof change !== "object") return [change];
    const scrub =
      change.id === "total"
        ? scrubCustomerTotal
        : change.id === "billTo" || change.id === "shipTo"
        ? scrubAddressee
        : PASSED_THROUGH.has(change.id)
        ? (side) => side
        : scrubCustomerItem;
    const before = scrub(change.before);
    const after = scrub(change.after);
    // An item whose only changes were internal (its cost, margin or markup)
    // has nothing left to show, and would read as an empty "Modified" line
    if (
      scrub === scrubCustomerItem &&
      isEmptyDiff(before) &&
      isEmptyDiff(after)
    ) {
      return [];
    }
    return [{ ...change, before, after }];
  });
}

/** A customer quote copy's data, scrubbed of everything above. */
export function scrubCustomerQuote(data) {
  return {
    ...data,
    items: Array.isArray(data.items)
      ? data.items.map(scrubCustomerItem)
      : data.items,
    total: scrubCustomerTotal(data.total),
    changes: scrubCustomerQuoteChanges(data.changes),
  };
}
